import {
  Badge,
  Button,
  EmptyState,
  StrongText,
  SurfaceRow,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  surfaceRowClassName,
} from '../../../../components/ui'
import { FunnelIcon } from '@heroicons/react/20/solid'
import { uiClasses } from '../../../shared/uiClasses'
import { PARTNER_COLORS } from './partnerColors'
import { TestCatalogRow } from './TestCatalogRow'
import type { TestCatalogItem } from './types'

interface TestsCatalogTableProps {
  filtered: TestCatalogItem[]
  orderedTestIds: Set<string>
  orderingTestId: string | null
  onOrderTest: (testId: string) => Promise<void>
}

export function TestsCatalogTable({
  filtered,
  orderedTestIds,
  orderingTestId,
  onOrderTest,
}: TestsCatalogTableProps) {
  if (filtered.length === 0) {
    return (
      <EmptyState
        className={`${surfaceRowClassName} tests-catalog-empty mt-4 py-7`}
        icon={FunnelIcon}
        title="No tests match these filters"
        description="Adjust your filters to see available tests."
        titleClassName={uiClasses.textSecondary}
        descriptionClassName={uiClasses.textSubtle}
        iconClassName={uiClasses.textFaint}
      />
    )
  }

  return (
    <div className="tests-catalog-scroll mt-4">
      <div className="tests-catalog-table-wrap">
        <Table
          className="tests-catalog-table"
          surface
          dense
          grid
          striped
        >
          <TableHeader className="tests-catalog-head">
            <TableRow>
              <TableHead className="tests-catalog-col">Test</TableHead>
              <TableHead className="tests-catalog-col">Category</TableHead>
              <TableHead className="tests-catalog-col">Partner</TableHead>
              <TableHead className="tests-catalog-col text-right">Price</TableHead>
              <TableHead className="tests-catalog-col text-right">Action</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {filtered.map((test) => (
              <TestCatalogRow
                key={test.id}
                test={test}
                isOrdering={orderingTestId === test.id}
                isOrdered={orderedTestIds.has(test.id)}
                onOrderTest={onOrderTest}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="tests-catalog-cards">
        {filtered.map((test) => {
          const isOrdering = orderingTestId === test.id
          const isOrdered = orderedTestIds.has(test.id)
          const actionLabel = isOrdering ? 'Ordering...' : isOrdered ? 'Ordered' : 'Order'

          return (
            <SurfaceRow key={test.id} as="article" className="tests-catalog-mobile-card">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                <StrongText>{test.name}</StrongText>
                  <p className={`mt-1 text-sm/6 ${uiClasses.textSubtle}`}>{test.description}</p>
                </div>
                <StrongText className="tabular-nums" size="base">${test.price}</StrongText>
              </div>

              <div className="tests-catalog-mobile-meta mt-3">
                <Badge variant="zinc">{test.category}</Badge>
                <Badge variant={PARTNER_COLORS[test.partner] ?? 'zinc'}>{test.partner}</Badge>
                {test.popular && <Badge variant="amber">Popular</Badge>}
              </div>

              <Button className="tests-order-btn mt-3" onClick={() => void onOrderTest(test.id)} disabled={isOrdering || isOrdered}>
                {actionLabel}
              </Button>
            </SurfaceRow>
          )
        })}
      </div>
    </div>
  )
}
